<template>
  <div class="timer" :class="modifiers">
    <div class="timer__circle">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 60 60"
        class="timer__bg"
      >
        <circle cx="30" cy="30" r="28" />
      </svg>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 60 60"
        class="timer__line"
        :style="circleAnimationDuration"
      >
        <circle cx="30" cy="30" r="28" />
      </svg>
    </div>
    <div class="timer__seconds">{{ seconds }}</div>
  </div>
</template>

<script>
export default {
  name: 'BaseTimer',
  props: {
    duration: {
      type: Number,
      default: 4,
    },
  },
  data() {
    return {
      onPause: false,
      isRunning: false,
      seconds: this.duration,
    };
  },
  staticData: () => ({
    timestamp: null,
    lastTimestamp: null,
    pauseTimestamp: null,
    raf: null,
  }),
  computed: {
    circleAnimationDuration() {
      const duration = this.duration * 1000;
      const delay = duration / 20;
      return { animationDuration: `${duration + delay}ms` };
    },
    modifiers() {
      return {
        timer_running: this.isRunning,
        timer_pause: this.onPause,
      };
    },
  },
  watch: {
    duration(value) {
      this.seconds = value;
      this.stop();
    },
  },
  beforeDestroy() {
    window.cancelAnimationFrame(this.raf);
  },
  methods: {
    start() {
      if (this.isRunning) return;
      this.isRunning = true;
      this.timestamp = null;
      this.seconds = this.duration;
      this.raf = window.requestAnimationFrame(this.calculate);
      this.$emit('start');
    },
    stop() {
      this.isRunning = false;
      this.timestamp = null;
      this.$emit('stop');
    },
    pause() {
      if (this.onPause) return;
      const diffTimestamp = this.lastTimestamp - this.timestamp;
      this.onPause = true;
      this.isRunning = false;
      this.pauseTimestamp = performance.now() - this.lastTimestamp + diffTimestamp;
      this.timestamp = null;
      this.$emit('pause');
    },
    resume() {
      this.onPause = false;
      this.isRunning = true;
      this.timestamp = performance.now() - this.pauseTimestamp;
      this.pauseTimestamp = null;
      this.raf = window.requestAnimationFrame(this.calculate);
      this.$emit('resume');
    },
    finish() {
      this.$emit('finish');
    },
    calculate(timestamp) {
      if (!this.isRunning) return;

      if (!this.timestamp) {
        this.timestamp = timestamp;
      }

      const diff = (timestamp - this.timestamp) / 10;

      if (diff >= 100) {
        this.seconds -= 1;
        this.timestamp = timestamp;
      }

      if (this.seconds <= 0) {
        this.stop();
        this.finish();
      } else {
        this.lastTimestamp = timestamp;
        this.raf = window.requestAnimationFrame(this.calculate);
      }
    },
  },
};
</script>

<style lang="stylus">
@keyframes timer_dashoffset
  to
    stroke-dashoffset 0

.timer
  position relative
  display inline-block
  vertical-align top
  width 3em
  height @width

  &__circle
    display block
    width 100%
    height 100%
    transform rotate(-90deg)

  &__bg
  &__line
    display block
    width 100%
    height 100%
    fill none
    stroke $c_bright
    stroke-width 4px
    stroke-linecap round

  &__bg
    abs top left
    opacity 0.2
    z-index 1

  &__line
    position relative
    stroke $c_bright
    stroke-dasharray 180
    stroke-dashoffset 180
    z-index 2
    ~/_pause &
    ~/_running &
      animation timer_dashoffset 1000ms linear both
    ~/_pause &
      animation-play-state paused

  &__seconds
    display flex
    align-items center
    justify-content center
    abs top left right bottom
    font-size 1.5em
    font-weight 900
</style>
